import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "./edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";

const filterE3StaticDatumsSchema = z.object({
  name_equal: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_equal: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_equal: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  ticker_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_equal: z.string().optional(),
  asset_class_1_equal: z.string().optional(),
  asset_class_2_equal: z.string().optional(),
});

export type FilterE3StaticDatumsSchema = z.infer<
  typeof filterE3StaticDatumsSchema
>;

const assetDepFields: readonly DepField<FilterE3StaticDatumsSchema>[] = [
  {
    name: "name_equal",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  { name: "isin_equal", key: "isin", extract: "text", bucket: "equals" },
  { name: "code_equal", key: "code", extract: "text", bucket: "equals" },
  {
    name: "ticker_contains",
    key: "ticker",
    extract: "text",
    bucket: "contains",
  },
];

const fields: Field[] = [
  {
    name: "name_equal",
    label: `Nom`,
    type: "asyncAutocomplete",
    base_url: "e3/e3_static_data",
    column_name: "name",
    filters: makeFiltersBuilder<FilterE3StaticDatumsSchema>(
      assetDepFields,
      "name_equal",
    ),
  },
  {
    name: "isin_equal",
    label: `ISIN`,
    type: "asyncAutocomplete",
    base_url: "e3/e3_static_data",
    column_name: "isin",
    filters: makeFiltersBuilder<FilterE3StaticDatumsSchema>(
      assetDepFields,
      "isin_equal",
    ),
  },
  {
    name: "code_equal",
    label: `Code`,
    type: "asyncAutocomplete",
    base_url: "e3/e3_static_data",
    column_name: "code",
    filters: makeFiltersBuilder<FilterE3StaticDatumsSchema>(
      assetDepFields,
      "code_equal",
    ),
  },
  {
    name: "ticker_contains",
    label: `Ticker`,
    type: "asyncAutocomplete",
    base_url: "e3/e3_static_data",
    column_name: "ticker",
    filters: makeFiltersBuilder(assetDepFields, "ticker_contains"),
  },
  {
    name: "currency_equal",
    label: `Devise`,
    type: "autocomplete",
    options: createCurrencyOption(),
  },
  {
    name: "asset_class_1_equal",
    label: `Classe d'actifs`,
    type: "select",
    useTranslation: true,
    client: "e3",
    table_name: "e3_static_data",
    language: "fr",
    translate_key: "asset_class_1",
  },
  {
    name: "asset_class_2_equal",
    label: `Sous-classe d'actifs`,
    type: "sectionSelect",
    useTranslation: true,
    useTransactionForSectionName: true,
    section_name_translation_key: "asset_class_1",
    section_name_table_name: "e3_static_data",
    client: "e3",
    table_name: "e3_static_data",
    language: "fr",
    translate_key: "asset_class_2",
    options: DEFAULT_ASSET_CLASS_OPTIONS,
  },
];

export function FilterE3StaticDatums() {
  return (
    <FilterTable
      schema={filterE3StaticDatumsSchema}
      client="e3"
      tableName="e3_static_data"
      fields={fields}
    />
  );
}
